import React from 'react';
import { Shield, Phone, Mail } from 'lucide-react';
import { Card } from '../components/ui/card';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl mb-6 shadow-lg">
              <Shield className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-gray-600">Last Updated: March 2025</p>
          </div>

          <Card className="p-8 lg:p-12 bg-white shadow-xl border-gray-100">
            <div className="prose prose-lg max-w-none">
              <Section title="1. Introduction">
                <p>
                  Welcome to PriEdu ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our platform to find tutors or access our free quiz-based tests.
                </p>
                <p>
                  Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the platform.
                </p>
              </Section>

              <Section title="2. Information We Collect">
                <p>We collect information that you provide directly to us, including:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Personal Information:</strong> Name, email address, phone number, and postal address</li>
                  <li><strong>Educational Details:</strong> Class/grade, subjects of interest, and learning preferences</li>
                  <li><strong>Location Data:</strong> City and area for matching with nearby tutors</li>
                  <li><strong>Account Information:</strong> Username, password, and profile details</li>
                  <li><strong>For Tutors:</strong> Qualifications, experience, identity documents, and teaching preferences</li>
                </ul>
              </Section>

              <Section title="3. How We Use Your Information">
                <p>We use the information we collect to:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Connect students with verified home tutors in their area</li>
                  <li>Provide free quiz-based tests and track learning progress</li>
                  <li>Process demo class bookings and tutor requests</li>
                  <li>Verify tutor identities and qualifications</li>
                  <li>Send important updates about bookings and classes</li>
                  <li>Improve our services and develop new features</li>
                  <li>Communicate about upcoming features like our mobile app for study materials</li>
                </ul>
              </Section>

              <Section title="4. Data Sharing">
                <p>We may share your information in the following circumstances:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>With Verified Tutors:</strong> Limited student/parent contact information is shared when a demo class is booked</li>
                  <li><strong>Service Providers:</strong> We work with trusted third-party services for payment processing, analytics, and communication</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                </ul>
              </Section>

              <Section title="5. Data Security">
                <p>
                  We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                </p>
              </Section>

              <Section title="6. Cookies and Tracking">
                <p>
                  We use cookies and similar tracking technologies to enhance your experience on our platform. You can control cookie preferences through your browser settings.
                </p>
              </Section>

              <Section title="7. User Rights">
                <p>You have the right to:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Access your personal information</li>
                  <li>Correct inaccurate or incomplete data</li>
                  <li>Request deletion of your data</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Withdraw consent where applicable</li>
                </ul>
              </Section>

              <Section title="8. Children's Privacy">
                <p>
                  Our platform is intended for students of all ages. For users under 18 years of age, we require parental or guardian consent. Parents and guardians have the right to review, delete, or refuse the collection of their child's personal information.
                </p>
              </Section>

              <Section title="9. Future Services">
                <p>
                  We are continuously working to enhance our platform. In the future, we plan to offer:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Expanded quiz-based test series with detailed analytics</li>
                  <li>Mobile application for study material delivery</li>
                  <li>Enhanced tuition teacher matching services</li>
                </ul>
                <p>
                  Any new features will be governed by this privacy policy, and we will notify users of any significant changes.
                </p>
              </Section>

              <Section title="10. Changes to This Policy">
                <p>
                  We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date.
                </p>
              </Section>

              <Section title="11. Contact Us">
                <p>
                  If you have questions or concerns about this privacy policy or our data practices, please contact us:
                </p>
                <div className="bg-blue-50 p-6 rounded-xl mt-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">94555 45273</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">support@priedu.com</span>
                    </div>
                  </div>
                </div>
              </Section>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
      {title}
    </h2>
    <div className="text-gray-700 leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

export default PrivacyPolicy;
